"use client"

import SkeletonCard from "@/components/SkeletonCard"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import api from "@/lib/axios"
import { User } from "lucide-react"
import { useQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"

export const getDataNilaiTalent = async (
  token: string,
  nomor_pendaftaran: string
) => {
  const response = await api.get(
    `/nilai-talent-wawancara-public?token=${token}&nomor_pendaftaran=${nomor_pendaftaran}`
  )
  return response.data?.data
}

export default function DetailNilaiTalentPublicPage() {
  const [searchParams] = useSearchParams()
  const tokenParams = searchParams.get("token") || ""
  const nomorPendaftaranParams = searchParams.get("nop") || ""

  const { data, isPending } = useQuery({
    queryKey: ["dataDetailNilaiTalent"],
    queryFn: () => getDataNilaiTalent(tokenParams, nomorPendaftaranParams),
    retry: false,
  })

  if (isPending)
    return (
      <div className="p-5 grid grid-cols-1 gap-y-3">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    )

  if (!data)
    return (
      <div className="h-screen flex justify-center items-center">
        Data tidak ditemukan!.
      </div>
    )

  return (
    <div className="py-10 max-w-[80vw] mx-auto">
      <section>
        <div className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Detail Peserta - {data?.dataNilai?.nama_lengkap}
        </div>
        <br />
      </section>

      <section className="grid grid-cols-1 gap-5">
        <div className="space-y-5">
          {/* Personal Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="h-4 w-4" />
                Biodata & Total Nilai
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Nama Lengkap
                  </label>
                  <p className="font-medium">{data?.dataNilai?.nama_lengkap}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Nomor Pendaftaran
                  </label>
                  <p className="font-medium">
                    {data?.dataNilai?.nomor_pendaftaran}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Status Kehadiran
                  </label>
                  <p className="font-medium">
                    {data?.dataNilai?.is_hadir_wawancara_talent == 1 ? (
                      <Badge variant={"success"}>Hadir</Badge>
                    ) : (
                      <Badge variant={"destructive"}>Tidak Hadir</Badge>
                    )}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Nilai Pewawancara 1
                  </label>
                  <p className="font-medium">{data?.dataNilai?.nilai_ver1}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Nilai Pewawancara 2
                  </label>
                  <p className="font-medium">{data?.dataNilai?.nilai_ver2}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Total Nilai
                  </label>
                  <p className="font-medium">{data?.dataNilai?.total_nilai}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              Detail Penilaian
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-8">
            {data?.dataNilaiDetail &&
              Object.keys(data?.dataNilaiDetail).map((key) => {
                const listNilai = data?.dataNilaiDetail[key]

                return (
                  <div>
                    <h1 className="font-semibold">{key}</h1>
                    <div className="flex-1 overflow-auto rounded-xl border relative">
                      <table className="table-auto w-full border-collapse border rounded-xl overflow-auto">
                        <thead>
                          <tr>
                            <th className="py-1 px-2 border font-semibold text-start">
                              Nama Komponen
                            </th>
                            <th className="py-1 px-2 border font-semibold text-start">
                              Nama Pewawancara 1
                            </th>
                            <th className="py-1 px-2 border font-semibold text-start">
                              Nilai Pewawancara 1
                            </th>
                            <th className="py-1 px-2 border font-semibold text-start">
                              Nama Pewawancara 2
                            </th>
                            <th className="py-1 px-2 border font-semibold text-start">
                              Nilai Pewawancara 2
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {listNilai &&
                            listNilai?.map(
                              (item: {
                                id_ref_komponen_penilaian_talent: number
                                kode: string
                                nama_ref_parent_komponen: string
                                nama_komponen: string
                                nilai_ver1: number
                                nama_ver1: string
                                nilai_ver2: number
                                nama_ver2: string
                              }) => {
                                return (
                                  <tr
                                    key={item?.id_ref_komponen_penilaian_talent}
                                  >
                                    <td className="py-1 px-2 border">
                                      {item?.nama_komponen}
                                    </td>
                                    <td className="py-1 px-2 border">
                                      {item?.nama_ver1}
                                    </td>
                                    <td className="py-1 px-2 border text-center">
                                      {item?.nilai_ver1}
                                    </td>
                                    <td className="py-1 px-2 border">
                                      {item?.nama_ver2}
                                    </td>
                                    <td className="py-1 px-2 border text-center">
                                      {item?.nilai_ver2}
                                    </td>
                                  </tr>
                                )
                              }
                            )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              })}
          </CardContent>
        </Card>
      </section>
    </div>
  )
}
